'use client';
import { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import s from './HeroCollage.module.css';

/*
 * <HeroCollage>
 *
 * Recreates the designer reference: a panoramic "scatter" of pre-styled
 * product-UI pieces floating in an aspect-locked stage. Each piece is
 * absolutely positioned by the percentages in its data item, so the
 * composition matches the reference exactly and scales with the stage
 * width. A faint pair of concentric arcs + a soft glow sit behind for
 * depth.
 *
 * The `primary` piece is the LCP candidate, so it is rendered as plain
 * markup with no framer-motion: a JS-gated `opacity: 0` would keep it
 * unpainted until hydration and push LCP out by seconds. Decorative
 * accents keep the staggered reveal + slow continuous bob, and only mount
 * on wide viewports so narrow ones never request them.
 *
 * ── PROPS ────────────────────────────────────────────────────────────
 * items: see /content/hero-collage.js for the shape.
 * className: string?  — extra class on the stage wrapper.
 */

function isPrimary(item) {
  return Boolean(item.primary || item.id === 'dashboard');
}

function pieceStyle(item) {
  return {
    ...(item.left != null ? { left: `${item.left}%` } : {}),
    ...(item.right != null ? { right: `${item.right}%` } : {}),
    ...(item.top != null ? { top: `${item.top}%` } : {}),
    ...(item.bottom != null ? { bottom: `${item.bottom}%` } : {}),
    width: `${item.width}%`,
    zIndex: item.z ?? 1,
  };
}

export function HeroCollage({ items, className = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.2 });
  // false until the client confirms a wide viewport — keeps decorative
  // accents out of the mobile document and network.
  const [showAccents, setShowAccents] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)');
    const sync = () => setShowAccents(!mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  const primary = items.filter(isPrimary);
  const accents = showAccents ? items.filter((item) => !isPrimary(item)) : [];

  return (
    <div ref={ref} className={`${s.stage} ${className}`}>
      {showAccents && (
        <>
          <svg
            className={s.arcs}
            viewBox="0 0 2044 698"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <ellipse cx="1050" cy="775" rx="835" ry="735" />
            <ellipse cx="1050" cy="805" rx="945" ry="825" />
          </svg>
          <div className={s.glow} aria-hidden="true" />
        </>
      )}

      {primary.map((item) => (
        <div
          key={item.src}
          className={`${s.piece} ${s.primary}`}
          {...(item.id ? { 'data-piece': item.id } : {})}
          style={pieceStyle(item)}
        >
          <img
            src={item.src}
            srcSet={item.srcSet}
            sizes={item.sizes}
            alt={item.alt}
            className={s.img}
            draggable={false}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width={item.intrinsicWidth || undefined}
            height={item.intrinsicHeight || undefined}
          />
        </div>
      ))}

      {accents.map((item, i) => {
        const floatClass =
          item.float === 'A' ? s.floatA : item.float === 'B' ? s.floatB : '';
        return (
          <motion.div
            key={item.src}
            className={`${s.piece} ${s.accent}`}
            {...(item.id ? { 'data-piece': item.id } : {})}
            style={pieceStyle(item)}
            initial={{ opacity: 0, y: 30, scale: 0.9 }}
            animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
            transition={{
              duration: 0.7,
              delay: (i + 1) * 0.085,
              // gentle overshoot "pop" so pieces settle in with life
              ease: [0.34, 1.46, 0.5, 1],
            }}
          >
            <img
              src={item.src}
              alt={item.alt}
              className={`${s.img} ${floatClass}`}
              draggable={false}
              loading="lazy"
              fetchPriority="low"
              decoding="async"
              style={floatClass ? { animationDelay: `${(i % 4) * 0.7}s` } : undefined}
            />
          </motion.div>
        );
      })}
    </div>
  );
}
